// Submission payload + validation for the unified renderer. Mirrors what the
// renderer shows: effective (prefill-applied) values, calc results, and the
// nested widget-instance tree.
import type { FieldDefinition, FieldValidation, FormConfig, WidgetNode } from '../shared/types';
import { asksNothing, dateBoundText, resolveDateBound } from '../shared/types';
import { addressFormat, compositeBlank, trimComposite } from '../shared/address';
import { fieldLocked, fieldRequired } from './config';
import { FormState, PublishedDef, WidgetInstance, valueKey, widgetForSection } from './model';
import { CtxEval, FormEval, renderableIn } from './evaluate';
import { STRINGS as S } from './strings';
import { isUsPhone, usPhoneDigits, wantsUsPhone } from './phone';
import { DATETIME_RE, datetimeDate, matrixAnswer, matrixBlank, matrixMeta, matrixMissingRows, scaleRange } from './questionTypes';

const isEmpty = (v: unknown) =>
  v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

const isComposite = (f: FieldDefinition) => f.input_type === 'name' || f.input_type === 'address';

/** Empty for payload purposes — a composite (or a matrix, P3) with all parts blank stays out. */
const blankOf = (f: FieldDefinition, v: unknown) =>
  (isComposite(f) ? compositeBlank(v) : f.input_type === 'matrix' ? matrixBlank(v) : isEmpty(v));

/**
 * The answer as it should be recorded. Composite sub-answers are held as typed
 * while the form is open — so that typing a space works — and the padding
 * comes off here, once, on the way into the submission.
 */
const answerOf = (f: FieldDefinition, v: unknown) => (isComposite(f) ? trimComposite(v) : v);

/**
 * Build the value tree that gets submitted:
 *  - order-scope effective values (non-empty; hidden mirrors included, matching
 *    the legacy behavior of carrying locked prefills even while hidden)
 *  - calc targets (computed values win)
 *  - one nested array per root widget scope
 *  - every widget node's countKey, and the key of each of its conditional counts
 * 'file' fields are excluded — the page merges uploaded URLs in after upload.
 */
export function buildSubmissionData(
  def: PublishedDef, state: FormState, ev: FormEval, skippedSections: Set<string> = new Set()
): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  const fileKeys = new Set(def.fields.filter((f) => f.input_type === 'file').map((f) => f.key));
  const skippedScopes = widgetScopesIn(def, skippedSections);

  for (const f of def.fields) {
    if (f.scope !== 'order' || fileKeys.has(f.key) || asksNothing(f.input_type)) continue;
    if (skippedSections.has(f.section)) continue; // a page a goto jumped over
    const v = ev.order.values[f.key];
    if (!blankOf(f, v)) data[f.key] = answerOf(f, v);
  }
  Object.assign(data, ev.calcs);

  const mapInstance = (inst: WidgetInstance, node: WidgetNode): Record<string, unknown> => {
    const ctx = ev.byInstance.get(inst.id);
    // Automatic descendant counts for THIS instance (this location's devices,
    // this device's lines, this location's lines across all its devices…), so
    // integrations can map them like any other value.
    const out: Record<string, unknown> = { ...(ev.instanceCounts.get(inst.id) || {}) };
    for (const f of def.fields) {
      if (f.scope !== node.scope || fileKeys.has(f.key) || asksNothing(f.input_type)) continue;
      const v = ctx ? ctx.values[f.key] : inst.values[f.key];
      if (!blankOf(f, v)) out[f.key] = answerOf(f, v);
    }
    for (const child of node.children || []) {
      out[child.scope] = (inst.children[child.scope] || []).map((c) => mapInstance(c, child));
    }
    return out;
  };

  for (const w of def.widgets || []) {
    if (!w.tree || skippedScopes.has(w.tree.scope)) continue;
    data[w.tree.scope] = (state.widgets[w.tree.scope] || []).map((inst) => mapInstance(inst, w.tree));
  }

  const walkCounts = (node: WidgetNode) => {
    if (node.countKey) data[node.countKey] = ev.counts[node.scope] ?? 0;
    for (const c of node.counts || []) data[c.key] = ev.conditionalCounts[c.key] ?? 0;
    (node.children || []).forEach(walkCounts);
  };
  for (const w of def.widgets || []) if (w.tree && !skippedScopes.has(w.tree.scope)) walkCounts(w.tree);

  return data;
}

/**
 * Every answer in the submission that was NOT on the respondent's screen when
 * they sent it — by path: `key` at the order scope, `scope.i.key` inside a
 * repeating item (`locations.1.devices.0.serial`), so a device hidden on the
 * second location and shown on the first is recorded for the second only.
 *
 * Hidden answers are still SUBMITTED (see `buildSubmissionData`: a locked
 * prefill, a default, a calculation, a rep code from a signed link all ride
 * along), so this is a record beside them rather than a filter over them. The
 * server keeps it as `_hidden`, and the document's list of answers leaves
 * these paths off: the PDF a customer is sent shows what they were shown.
 *
 * Visibility is `renderableIn`'s — the same check the renderer draws with and
 * the validator waives requiredness by — so a field inside a hidden section,
 * card or container counts as hidden too. Only answers the payload carries are
 * listed; a page a goto skipped carries none.
 */
export function hiddenAtSubmit(
  def: PublishedDef, state: FormState, ev: FormEval, cfg: FormConfig, skippedSections: Set<string> = new Set()
): string[] {
  const out: string[] = [];
  const skippedScopes = widgetScopesIn(def, skippedSections);
  const calcKeys = new Set(Object.keys(ev.calcs));

  for (const f of def.fields) {
    if (f.scope !== 'order' || asksNothing(f.input_type) || skippedSections.has(f.section)) continue;
    const answered = f.input_type === 'file'
      ? (state.files[valueKey(null, f.key)] || []).length > 0
      : calcKeys.has(f.key) || !blankOf(f, ev.order.values[f.key]);
    if (answered && !renderableIn(f, ev.order, cfg, ev)) out.push(f.key);
  }

  const walk = (inst: WidgetInstance, node: WidgetNode, prefix: string) => {
    const ctx = ev.byInstance.get(inst.id);
    for (const f of def.fields) {
      if (f.scope !== node.scope || f.input_type === 'file' || asksNothing(f.input_type)) continue;
      const v = ctx ? ctx.values[f.key] : inst.values[f.key];
      if (blankOf(f, v)) continue;
      if (!ctx || !renderableIn(f, ctx, cfg, ev)) out.push(`${prefix}${f.key}`);
    }
    for (const child of node.children || []) {
      (inst.children[child.scope] || []).forEach((c, i) => walk(c, child, `${prefix}${child.scope}.${i}.`));
    }
  };
  for (const w of def.widgets || []) {
    if (!w.tree || skippedScopes.has(w.tree.scope)) continue;
    (state.widgets[w.tree.scope] || []).forEach((inst, i) => walk(inst, w.tree, `${w.tree.scope}.${i}.`));
  }
  return out;
}

export interface ValidationError {
  key: string;
  label: string;
  /** '' for the order scope; else a readable instance path like "Location 2 › Device 1" */
  where: string;
  /** absent = "please fill this in"; present = a constraint the answer breaks */
  message?: string;
  /**
   * Where the error lands on screen: the field's value key (instance-scoped
   * inside a widget, `inst.key`), with a dotted part for one sub-input of a
   * composite (`shipping.postalCode`, `name.firstName`). This is the key of
   * the `FieldErrors` map the renderer reads.
   */
  path: string;
}

/** Per-control error text, keyed like `ValidationError.path`. */
export type FieldErrors = Record<string, string>;

/** The text under the control: the constraint it breaks, or that it is required. */
export const errorText = (e: Pick<ValidationError, 'message'>): string => e.message || S.required;

/** The summary's line for one error: "Email must be a valid email address". */
export function errorLine(e: ValidationError): string {
  const name = e.where ? `${e.label} (${e.where})` : e.label;
  return e.message ? `${name} ${e.message}` : name;
}

/** The map the renderer threads down — first error per control wins. */
export function toFieldErrors(errors: ValidationError[]): FieldErrors {
  const out: FieldErrors = {};
  for (const e of errors) if (!(e.path in out)) out[e.path] = errorText(e);
  return out;
}

/** Validate and hand back the per-control map in one call. */
export function collectFieldErrors(def: PublishedDef, state: FormState, ev: FormEval, cfg: FormConfig): FieldErrors {
  return toFieldErrors(validateForm(def, state, ev, cfg));
}

/**
 * One readable sentence out of a batch of validation errors — the shape the
 * page showed before errors landed on their fields, kept for the server's
 * `error` string and anything still reading a single line.
 */
export function formatValidationErrors(errors: ValidationError[]): string {
  const missing = errors.filter((x) => !x.message);
  const broken = errors.filter((x) => x.message);
  return [
    missing.length ? `${S.fillIn} ${missing.map(errorLine).join(', ')}` : '',
    broken.length ? `${S.check} ${broken.map(errorLine).join('; ')}` : '',
  ].filter(Boolean).join(' — ');
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Does a non-empty answer break the field's constraints (ui_meta.validation +
 * the input type's own shape)? Mirrors server lib/validateSubmission.ts.
 */
export function constraintMessage(f: FieldDefinition, v: unknown, required = false): string | null {
  const rules = f.ui_meta?.validation || {};
  const custom = rules.message?.trim() || null;
  const s = Array.isArray(v) ? '' : String(v ?? '');

  // P3: the four new shapes. Each returns before the text rules below.
  if (f.input_type === 'scale') {
    const { min, max } = scaleRange(f.ui_meta);
    const n = typeof v === 'number' ? v : Number(s.trim());
    if (!Number.isInteger(n) || n < min || n > max) return custom || S.v.scale(min, max);
    return null;
  }
  if (f.input_type === 'matrix') {
    const meta = matrixMeta(f.ui_meta);
    const o = matrixAnswer(v);
    if (!o) return custom || S.v.matrixRows;
    const cols = new Set(meta.columns.map((c) => c.value));
    for (const x of Object.values(o)) {
      for (const p of Array.isArray(x) ? x : [x]) {
        if (p !== undefined && p !== null && p !== '' && cols.size && !cols.has(String(p))) return custom || S.v.choice;
      }
    }
    if (required && matrixMissingRows(meta, v).length) return custom || S.v.matrixRows;
    return null;
  }
  if (f.input_type === 'ranking') {
    if (!Array.isArray(v)) return custom || S.v.rankingAll;
    if (new Set(v).size !== v.length) return custom || S.v.rankingDup;
    const allowed = new Set((f.options || []).map((o) => o.value));
    if (allowed.size) {
      for (const p of v) if (!allowed.has(String(p))) return custom || S.v.choice;
      if (required && v.length < allowed.size) return custom || S.v.rankingAll;
    }
    return null;
  }
  if (f.input_type === 'datetime') {
    if (!DATETIME_RE.test(s)) return custom || S.v.datetime;
    const msg = dateWindowMessage(rules, datetimeDate(s));
    return msg ? custom || msg : null;
  }

  if (f.input_type === 'number') {
    const n = Number(s);
    if (!Number.isFinite(n)) return custom || S.v.number;
    if (rules.min !== undefined && n < rules.min) return custom || S.v.min(rules.min);
    if (rules.max !== undefined && n > rules.max) return custom || S.v.max(rules.max);
    return null;
  }
  if (f.input_type === 'email' && !EMAIL_RE.test(s)) return custom || S.v.email;
  if (f.input_type === 'url' && !/^https?:\/\/\S+$/i.test(s)) return custom || S.v.url;
  // A US phone is stored as digits by its control; a resumed or prefilled
  // answer is read the same way, so the count is always on the digits.
  if (wantsUsPhone(f) && !isUsPhone(usPhoneDigits(s))) return custom || S.v.phone;
  if (f.input_type === 'date') {
    const msg = dateWindowMessage(rules, s);
    if (msg) return custom || msg;
  }
  if (rules.minLen !== undefined && s.length < rules.minLen) return custom || S.v.minLen(rules.minLen);
  if (rules.maxLen !== undefined && s.length > rules.maxLen) return custom || S.v.maxLen(rules.maxLen);
  if (rules.pattern) {
    try {
      if (!new RegExp(`^(?:${rules.pattern})$`).test(s)) return custom || S.v.pattern;
    } catch { /* an invalid pattern never blocks an answer */ }
  }
  return null;
}

/**
 * Is a date answer outside the window the field allows? Relative bounds
 * ('today', 'today+10') resolve against the day the form is being filled in,
 * so the same rule keeps meaning the same thing tomorrow.
 */
function dateWindowMessage(rules: FieldValidation, s: string): string | null {
  const min = resolveDateBound(rules.dateMin);
  const max = resolveDateBound(rules.dateMax);
  if (min && s < min) return S.v.dateMin(dateBoundText(rules.dateMin!));
  if (max && s > max) return S.v.dateMax(dateBoundText(rules.dateMax!));
  return null;
}

/**
 * The parts of a required composite (name/address) answer still blank, each
 * with the caption the renderer shows under that sub-input. Which address
 * parts are demanded follows the selected country's format.
 */
export function compositeMissing(f: FieldDefinition, v: unknown): { sub: string; label: string }[] {
  const o = (v && typeof v === 'object' && !Array.isArray(v) ? v : {}) as Record<string, unknown>;
  const blank = (k: string) => !String(o[k] ?? '').trim();
  let parts: [string, string][];
  if (f.input_type === 'name') {
    parts = [['firstName', S.sub.firstName], ['lastName', S.sub.lastName]];
  } else {
    const fm = addressFormat(String(o.country || f.ui_meta?.address?.defaultCountry || '') || undefined);
    parts = [];
    // A shipping label needs a recipient; Attention and Phone stay optional.
    if (f.ui_meta?.address?.kind === 'shipping') parts.push(['company', S.sub.company]);
    parts.push(['addressLine1', S.sub.addressLine1], ['city', fm.cityLabel]);
    if (fm.stateLabel && fm.stateRequired) parts.push(['state', fm.stateLabel]);
    if (fm.postalLabel) parts.push(['postalCode', fm.postalLabel]);
    parts.push(['country', S.sub.country]);
  }
  return parts.filter(([k]) => blank(k)).map(([sub, label]) => ({ sub, label }));
}

/**
 * Answer validation, context by context — requiredness for exactly the fields
 * the renderer shows as required, plus each answered field's constraints.
 * Native constraint validation is off on the public form (`noValidate`), so
 * this is THE check: what it returns is what lands under each control, in
 * document order.
 */
export function validateForm(
  def: PublishedDef, state: FormState, ev: FormEval, cfg: FormConfig, skippedSections: Set<string> = new Set()
): ValidationError[] {
  const errors: ValidationError[] = [];
  const fileKeys = new Set(def.fields.filter((f) => f.input_type === 'file').map((f) => f.key));
  const skippedScopes = widgetScopesIn(def, skippedSections);

  const checkCtx = (scope: string, ctx: CtxEval, where: string, instanceId: string | null) => {
    for (const f of def.fields) {
      if (f.scope !== scope) continue;
      if (scope === 'order' && skippedSections.has(f.section)) continue; // a page a goto jumped over
      if (f.input_type === 'payment') continue; // paying happens after submit
      if (asksNothing(f.input_type)) continue;   // presentation has no answer
      if (!renderableIn(f, ctx, cfg, ev)) continue;
      if (ev.calcs[f.key] !== undefined) continue; // computed
      if (ctx.locked[f.key]) continue;             // mirrored
      // A LOCKED field cannot be answered, so it cannot be demanded: locked and
      // empty and required is a box nobody can get out of. Same reading as a
      // mirrored one, and it covers Build's Locked answer as well as a rule's.
      if (fieldLocked(f, ctx)) continue;
      const v = fileKeys.has(f.key)
        ? (state.files[valueKey(instanceId, f.key)] || []).length || undefined
        : ctx.values[f.key];
      const composite = f.input_type === 'name' || f.input_type === 'address';
      const path = valueKey(instanceId, f.key);
      if (composite ? compositeBlank(v) : isEmpty(v)) {
        if (!fieldRequired(f, cfg, ctx)) continue;
        if (composite) {
          // Every part is blank: one error per demanded part, so each lands
          // under its own sub-input rather than once under the group.
          for (const p of compositeMissing(f, v)) {
            errors.push({ key: f.key, label: `${f.label} — ${p.label}`, where, path: `${path}.${p.sub}` });
          }
        } else {
          errors.push({ key: f.key, label: f.label, where, path });
        }
        continue;
      }
      if (composite) {
        if (!fieldRequired(f, cfg, ctx)) continue;
        for (const p of compositeMissing(f, v)) {
          errors.push({ key: f.key, label: `${f.label} — ${p.label}`, where, path: `${path}.${p.sub}` });
        }
      } else if (!fileKeys.has(f.key)) {
        const msg = constraintMessage(f, v, fieldRequired(f, cfg, ctx));
        if (msg) errors.push({ key: f.key, label: f.label, where, message: msg, path });
      }
    }
  };

  checkCtx('order', ev.order, '', null);

  const walk = (inst: WidgetInstance, node: WidgetNode, crumb: string) => {
    const ctx = ev.byInstance.get(inst.id);
    if (ctx) checkCtx(node.scope, ctx, crumb, inst.id);
    for (const child of node.children || []) {
      (inst.children[child.scope] || []).forEach((c, i) =>
        walk(c, child, `${crumb} › ${child.itemLabel} ${i + 1}`));
    }
  };
  for (const w of def.widgets || []) {
    if (!w.tree || skippedScopes.has(w.tree.scope)) continue;
    (state.widgets[w.tree.scope] || []).forEach((inst, i) => walk(inst, w.tree, `${w.tree.itemLabel} ${i + 1}`));
  }

  return errors;
}

/**
 * The root scope of every widget placed in one of these sections — what a
 * skipped page takes off the form besides its order-scope fields.
 */
function widgetScopesIn(def: PublishedDef, sectionIds: Set<string>): Set<string> {
  const scopes = new Set<string>();
  if (!sectionIds.size) return scopes;
  for (const s of def.layout?.sections || []) {
    if (!sectionIds.has(s.id) || (s.type !== 'widget' && s.type !== 'locations')) continue;
    const tree = widgetForSection(def.widgets, s)?.tree;
    if (tree) scopes.add(tree.scope);
  }
  return scopes;
}
